import ENDPOINTS from '.';

// Types for Wrapped 2025 API
export interface SeasonDetail {
  seasonNo: number;
  rank: number | null;
  points: string;
  status: 'participant' | 'rising_star' | 'top_performer' | 'skipped';
}

export interface LuckyDrawWin {
  seasonNo: number;
  prizeLabel: string;
  amountLamports: string;
}

export interface BestDay {
  date: string;
  itemsJunked: number;
  chestsOpened: number;
}

export interface StreakDates {
  start: string;
  end: string;
}

export interface NftMinted {
  seasonNo: number;
  tier: string;
  mintAddress: string | null;
}

export interface WrappedData {
  // Identity
  userId: number;
  walletAddress: string;
  joinDate: string;
  daysSinceJoin: number;
  platformAgeAtJoin: number;

  // Activity Metrics
  tokensJunked: number;
  nftsJunked: number;
  totalItemsJunked: number;
  itemsPerDay: number;
  chestsOpened: number;

  // Financial
  solReclaimed: string;
  averageSolPerJunk: string;
  biggestSingleReclaim: string;

  // Seasonal
  seasonsParticipated: number;
  totalSeasons: number;
  seasonDetails: SeasonDetail[];
  highestSeasonRank: { seasonNo: number; rank: number } | null;

  // Points & Lottery
  lifetimePoints: string;
  pointsContributedToLottery: string;
  luckyDrawWins: LuckyDrawWin[];

  // Streaks & Engagement
  daysActive: number;
  longestStreak: number;
  streakDates: StreakDates | null;
  bestDay: BestDay | null;

  // Community Standing
  overallRank: number | null;
  totalUsers: number;
  percentile: number;

  // Badges
  badges: string[];

  // Personality
  personalityType: string;
  personalityTraits: string[];

  // NFTs
  nftsMinted: NftMinted[];

  // Rewards status
  viewPointsAwarded: boolean;
  sharePointsAwarded: boolean;
}

export interface WrappedAccessResponse {
  allowed: boolean;
  reason?: string;
}

export interface WrappedCompleteResponse {
  success: boolean;
  pointsAwarded: number;
  alreadyCompleted: boolean;
}

export interface WrappedShareResponse {
  success: boolean;
  pointsAwarded: number;
  alreadyShared: boolean;
  error?: string;
}

export async function checkWrappedAccess(): Promise<WrappedAccessResponse> {
  const response = await fetch(ENDPOINTS.wrappedAccess, {
    credentials: 'include',
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    console.error('[API] checkWrappedAccess - Error:', { status: response.status, message });
    throw new Error(message);
  }

  return json.data || json;
}

export async function getWrappedData(): Promise<WrappedData> {
  const response = await fetch(ENDPOINTS.wrappedData, {
    credentials: 'include',
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    console.error('[API] getWrappedData - Error:', { status: response.status, message });
    throw new Error(message);
  }

  return json.data || json;
}

export async function markWrappedComplete(): Promise<WrappedCompleteResponse> {
  const response = await fetch(ENDPOINTS.wrappedComplete, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    console.error('[API] markWrappedComplete - Error:', { status: response.status, message });
    throw new Error(message);
  }

  return json.data || json;
}

export async function markWrappedShared(tweetUrl: string): Promise<WrappedShareResponse> {
  const response = await fetch(ENDPOINTS.wrappedShare, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tweetUrl }),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = json?.message || json?.error || response.statusText;
    console.error('[API] markWrappedShared - Error:', { status: response.status, message });
    throw new Error(message);
  }

  return json.data || json;
}
